import type { BuyerWalletPayment, SavedCard } from "./wallet-api";
import { isReverifyRequired, WalletApiFailure } from "./wallet-api";
import type { CoreContext } from "./context";
import type { EmailFlows } from "./email-flows";
import {
  brandName,
  cardLabel,
  isEmailValid,
  messageFromError,
} from "./format";
import type { IdentityFlows } from "./identity-flows";
import { CARD_REUSE_CONSENT } from "./ports";
import type { CardIsland } from "./ports";
import type { BankReason, Busy, CoreState } from "./state";
import { canPayNow, destinationOf, isZeroTotal, limitProblemOf } from "./state";
import { isSessionFinal } from "./state-machine";

/**
 * Paying. Every method takes the same road: a method on the platform —
 * a saved card, a card typed now, a wallet sheet's — cloned onto the
 * organizer's account by the engine and confirmed there (by the engine
 * itself, for a saved card, when it can be), with the bank's check drawn in
 * the checkout when it wants one, and the engine asked to issue the tickets
 * the moment the money has moved.
 */
/** An answer that never came, or a failure after which the engine may
 *  still have charged: not the engine's considered refusal (a 4xx). */
const answerLost = (error: unknown) =>
  !(error instanceof WalletApiFailure) ||
  error.failure.status === 0 ||
  error.failure.status >= 500;

export function createPayFlows(
  ctx: CoreContext,
  identity: IdentityFlows,
  email: EmailFlows,
) {
  const { deps, config, live, set, dispatch, sessionWrite, emit, refs } = ctx;
  const { wallet } = deps.engine;
  const { broker } = deps.identity;
  const { sessionId, hostIdentity } = config;

  /** Shows the bank's page and resolves once the buyer has answered it;
   *  what the bank decided is read from the intent afterwards. */
  const throughBank = (url: string, reason: BankReason) =>
    new Promise<void>((answered) => {
      refs.bankAnswered = answered;
      set({ bank: { url, reason } });
    });

  const bankDone = () => {
    set({ bank: null });
    refs.bankAnswered?.();
    refs.bankAnswered = null;
  };

  /** A payment failed: the one notice, and the page is told. */
  const failed = (message: string) => {
    set({ notice: message });
    emit({ type: "failed", message, code: null });
  };

  /** A card or wallet sheet is confirming before the method exists. */
  const methodPending = (on: boolean) => {
    set(on ? { busy: "card", payingFlag: true } : { busy: null });
  };

  /**
   * The money moved — the processor said so, after the bank's check if it
   * wanted one — so the checkout is issuing: the engine is asked to settle
   * now, reading the payment from the processor itself, rather than when the
   * processor's webhook reaches it. Its answer is applied as a write's (a
   * read made before it cannot undo it), and only when it is final: a
   * finalize that could not complete the session (the processor out of
   * reach, the tickets' capacity in question) leaves the finalizing poll to
   * hear the outcome, as it would have without it.
   */
  /**
   * The engine was asked to confirm the payment and its answer never came:
   * the network dropped it, or the engine failed after Stripe had charged.
   * Whether the money moved is Stripe's to say, not the lost answer's, so
   * the engine is asked to finalize (it reads the payment from Stripe
   * itself): a session it completed, or found being paid, goes on to the
   * tickets like any paid one; only one still open for payment fails.
   */
  const paidAfterAll = async (): Promise<boolean> => {
    const { buyer } = deps.engine;
    if (!buyer) return false;
    const apply = sessionWrite();
    const result = await buyer.finalize(sessionId).catch(() => null);
    const session = result?.data;
    if (!session || session.status === "open") return false;
    apply({ type: "session_updated", session });
    return true;
  };

  const paid = (intentId: string) => {
    refs.paymentIntentId = intentId;
    dispatch({ type: "payment_confirmed" });
    const { buyer } = deps.engine;
    if (!buyer) return;
    const settle = async () => {
      const apply = sessionWrite();
      const result = await buyer.finalize(sessionId);
      if (result.data && isSessionFinal(result.data))
        apply({ type: "session_updated", session: result.data });
    };
    // Whatever becomes of it, the payment stands: nothing here can fail it.
    void settle().catch(() => undefined);
  };

  /**
   * The engine confirms a saved card's payment itself, in the pay request —
   * a round trip to the processor sooner than the browser's — when the
   * renderer can make a device session for the processor's fraud screening
   * on the organizer's account (named by the wallet setup at page open, or
   * an earlier pay answer). The renderer began making it once the card was
   * on offer (`prepareDeviceSession` below), so the press seldom waits.
   * Otherwise — no such renderer (the app), the account not yet known, or
   * none made — the payment is confirmed here, as every other method's is:
   * the screening never loses the buyer's device for the sake of speed. A
   * card typed now or a wallet sheet's method was made in this browser, and
   * is confirmed where it was made.
   */
  const deviceSession = async (): Promise<string | null> => {
    const make = deps.payments.deviceSession;
    const account = refs.paymentAccount;
    if (!make || !account) return null;
    return make(account).catch(() => null);
  };

  /**
   * Making a device session can take about a second on a phone, so the
   * renderer begins one as soon as a payment could use it — the
   * organizer's account named, a saved card on offer, something to pay,
   * nothing under way — and the press finds it made: once that is so
   * (checkout-reactions.ts), and again after each attempt that did not
   * pay, since a session goes with one payment only. Asking while one is
   * on hand begins nothing; a renderer that cannot make one (the app) is
   * never asked.
   */
  const prepareDeviceSession = (view: CoreState) => {
    const prepare = deps.payments.prepareDeviceSession;
    const account = refs.paymentAccount;
    if (
      !prepare ||
      !account ||
      view.checkout.kind !== "cart" ||
      isZeroTotal(view) ||
      !view.attested?.saved_cards.length ||
      view.busy !== null
    )
      return;
    prepare(account);
  };

  const confirmOnConnected = async (
    payment: BuyerWalletPayment,
    setupChallenged: boolean,
  ) => {
    // If the bank wants to check the payment, its page is drawn in the
    // checkout under the reason — and, on a card the bank challenged a
    // moment ago as it was saved, why it asks a second time.
    const reason = setupChallenged ? "payment_again" : "payment";
    const confirmed = await deps.payments
      .confirmPayment({
        payment,
        reason,
        bank: (url) => throughBank(url, reason),
      })
      .finally(() => {
        // Status reconciliation may finish even if the bank's return message
        // never arrives. Put the challenge away on success and refusal alike.
        refs.bankAnswered = null;
        set({ bank: null });
      });
    paid(confirmed.intentId);
  };

  /** Every method pays the same way: cloned onto the organizer, charged
   *  there. */
  const payWith = async (
    view: CoreState,
    paymentMethodId: string,
    keep: boolean,
    via: Busy,
    label?: string | null,
    setupChallenged = false,
    /** The host has already proved the buyer again for this payment. */
    renewed = false,
  ): Promise<void> => {
    if (!wallet) return;
    // A wallet sheet is not gated by the Pay button: a cart outside the
    // event's limits is refused here, in words, before the engine would.
    const outsideLimits = limitProblemOf(view);
    if (outsideLimits) {
      set({ notice: outsideLimits, busy: null });
      return;
    }
    if (hostIdentity && !view.attested) {
      set({ notice: "Confirm it's you with the code first." });
      return;
    }
    if (label !== undefined) set({ methodLabel: label });
    // The press is answered at once: "Processing payment" from here, and
    // whatever comes before the engine prepares the payment (the consent
    // below, when one is asked) happens behind it.
    set({ busy: via, payingFlag: true, notice: null });
    refs.paidWith = paymentMethodId;
    // The engine was asked to confirm the payment and has not answered.
    let engineConfirming = false;
    try {
      // Keeping a card is the identity plane's consent, not the browser's
      // tick: an attested buyer who asks to keep a new card grants it
      // first. A card already in the wallet asks nothing: the engine keeps
      // it whatever this payment says (`keep` is sticky there). A
      // recognised session grants it without a code; a stale one gets the
      // code, and the payment resumes from the slot once it is entered.
      // A host answers for itself (it knows nothing of the broker's held
      // consents, so it is asked each time); a broker only once connected.
      const kept =
        view.attested?.saved_cards.some(
          (card) => card.id === paymentMethodId,
        ) ?? false;
      let keepCard = keep;
      if (
        keep &&
        !kept &&
        view.attested &&
        (!broker || view.identity.connected) &&
        !view.identity.consentsHeld.includes(CARD_REUSE_CONSENT)
      ) {
        const consent = await deps.identity
          .checkoutToken({
            sessionId,
            rememberMe: true,
            consents: [CARD_REUSE_CONSENT],
          })
          // Keeping the card is never a reason not to pay. With the identity
          // plane out of reach (or a host that cannot grant it without a
          // code) the payment goes ahead and keeps nothing new.
          .catch(() => null);
        // The status gives way to the code (the payment is not over, only
        // waiting for the buyer), and comes back once it is entered.
        if (consent && "code" in consent && broker) {
          set({ outcome: { kind: "pay", paymentMethodId, keep } });
          return;
        }
        if (consent && "token" in consent)
          await identity.attestWith(consent.token);
        else keepCard = false;
      }
      const device = kept ? await deviceSession() : null;
      engineConfirming = device !== null;
      const payment = await wallet.walletPay(sessionId, {
        payment_method_id: paymentMethodId,
        keep: keepCard,
        ...(device
          ? { confirm: "server" as const, radar_session: device }
          : {}),
      });
      engineConfirming = false;
      refs.paymentAccount = {
        publishable_key: payment.publishable_key,
        connected_account_id: payment.connected_account_id,
      };
      // The engine confirmed it and the money moved: nothing is left for the
      // browser but the tickets. A bank that wants its check (the engine
      // says `requires_action`) is drawn as after the browser's own
      // confirmation, which then reads the payment rather than confirming it
      // again; a card that refused was the engine's 422, caught below as
      // the browser's refusal is.
      const status = payment.payment_intent_status;
      if (status === "succeeded" || status === "processing")
        paid(intentIdOf(payment.payment_intent_client_secret));
      else await confirmOnConnected(payment, setupChallenged);
    } catch (error) {
      if (isReverifyRequired(error) && broker && view.identity.connected) {
        await identity.startCode(
          view,
          { reverify: true },
          { kind: "pay", paymentMethodId, keep },
        );
      } else if (isReverifyRequired(error) && !broker && !renewed) {
        // The host proves the buyer again itself; the payment is tried once
        // more on the fresh attestation, and fails as it would have if not.
        const answer = await deps.identity
          .checkoutToken({ sessionId, consents: [], reverify: true })
          .catch(() => null);
        const attested =
          answer && "token" in answer
            ? await identity.attestWith(answer.token).catch(() => null)
            : null;
        if (attested?.attested)
          await payWith(
            view,
            paymentMethodId,
            keep,
            via,
            label,
            setupChallenged,
            true,
          );
        else failed("We couldn't confirm it's you again. Try another card.");
      } else if (engineConfirming && answerLost(error) && (await paidAfterAll())) {
        // The engine had confirmed it after all: the tickets follow.
      } else {
        failed(
          messageFromError(
            error,
            "The payment didn't go through. Try another card.",
          ),
        );
      }
    } finally {
      set({ busy: null });
      // An attempt that did not pay took its session with it (if it asked
      // for one): the next is begun now. A paid one is finalizing.
      prepareDeviceSession(live());
    }
  };

  /** A saved Lissen card pays, and stays kept: it is in the wallet already,
   *  so no consent is asked for it. */
  const paySaved = (view: CoreState, card: SavedCard) =>
    payWith(view, card.id, true, "lissen", cardLabel(card));

  /**
   * A card typed now: the card island sets it up on the platform SetupIntent
   * (the bank may check it, drawn here under "card"), and hands back only
   * the method it made, which then pays exactly as every other method.
   * `brandHint` is what the card fields said while typing, for when the
   * processor names no card.
   */
  const payWithNewCard = async (
    view: CoreState,
    card: CardIsland,
    brandHint: string | null,
  ) => {
    if (!view.setup || !canPayNow(view, config) || refs.submittingCard) return;
    refs.submittingCard = true;
    methodPending(true);
    try {
      const result = await card.submit({
        email: destinationOf(view),
        bank: (url) => throughBank(url, "card"),
      });
      if ("error" in result) {
        failed(result.error);
        methodPending(false);
        return;
      }
      const label = result.card
        ? cardLabel(result.card)
        : brandHint && brandHint !== "unknown"
          ? brandName(brandHint)
          : null;
      void payWith(
        view,
        result.paymentMethodId,
        view.remember,
        "card",
        label,
        result.challenged,
      );
    } catch (error) {
      failed(messageFromError(error, "That card was not accepted."));
      methodPending(false);
    } finally {
      refs.submittingCard = false;
    }
  };

  /** A wallet sheet confirmed the platform SetupIntent: the method it made
   *  pays exactly as a typed card does, and the address the sheet collected
   *  fills the field when nothing was typed. */
  const payExpress = async (
    view: CoreState,
    paymentMethodId: string,
    sheetEmail: string | null,
    label: string | null,
  ) => {
    set({ notice: null });
    if (hostIdentity) {
      // The address is the page's verified one; the sheet's is not asked.
      await payWith(view, paymentMethodId, false, "express", label);
      return;
    }
    if (
      view.email.masked ||
      !view.email.value ||
      !isEmailValid(view.email.value)
    ) {
      if (!sheetEmail || !isEmailValid(sheetEmail)) {
        set({
          notice: "Enter your email for the tickets, then try again.",
          busy: null,
        });
        return;
      }
      set({ email: { value: sheetEmail, chip: true, source: "typed" } });
      await email.commitEmail(view, sheetEmail);
    }
    await payWith(view, paymentMethodId, view.remember, "express");
  };

  /** The code was entered: attest, then finish whatever it was asked for. */
  const codeVerified = async (view: CoreState, token: string | null) => {
    if (!token) {
      set({ notice: "We couldn't confirm that code. Try again." });
      return;
    }
    const { outcome } = view;
    try {
      if (isZeroTotal(view) && outcome.kind === "remember") {
        set({ remembered: "email" });
        return;
      }
      const result = await identity.attestWith(token);
      if (outcome.kind === "remember")
        // The engine joined the card to the wallet if it could; a buyer who
        // already had a wallet on another Customer keeps only the address.
        set({
          remembered: result?.saved_cards.some(
            (card) => card.id === refs.paidWith,
          )
            ? "card"
            : "email",
        });
      if (outcome.kind === "pay" && result?.attested)
        await payWith(view, outcome.paymentMethodId, outcome.keep, "lissen");
    } catch (error) {
      set({
        notice: messageFromError(error, "We couldn't reach your Lissen wallet."),
      });
    }
  };

  return {
    throughBank,
    bankDone,
    failed,
    methodPending,
    prepareDeviceSession,
    payWith,
    paySaved,
    payWithNewCard,
    payExpress,
    codeVerified,
  };
}

export type PayFlows = ReturnType<typeof createPayFlows>;

/** `pi_…` out of the intent's client secret (`pi_…_secret_…`). */
const intentIdOf = (secret: string) => {
  const at = secret.indexOf("_secret_");
  return at > 0 ? secret.slice(0, at) : "";
};
